import { PARTIAL_DATA_TABLE_HEADERS as HEADERS } from "../../../../constants/analyticsBatches";

import React from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import Label from "../../../../components/Label/Label";
import OverflowListSummary from "../../../../components/OverflowListSummary/OverflowListSummary";
import {
  isTruthyFlag,
  normalizeMissingParameterNames,
  RECENTLY_VISITED_ROW_BACKGROUND,
} from "../../../../utils/batchesUtility";

const PartialDataTable = ({
  rows = [],
  page = 1,
  pageSize = 50,
  loading = false,
  hasUnitOperation = false,
  highlightedBatchId = "",
  onBatchClick = () => {},
  sortConfig = { sort_by: "", sort_order: "asc" },
  onSort = () => {},
}) => (
  <Box width="100%">
    <TableContainer
      component={Paper}
      sx={{
        borderRadius: "8px",
        maxHeight: "calc(100vh - 290px)",
        overflowY: "auto",
        overflowX: "hidden",
        boxShadow: "none",
      }}
    >
      <Table
        stickyHeader
        sx={{
          width: "100%",
          tableLayout: "fixed",
          "& th, & td": { overflow: "hidden" },
        }}
      >
        <TableHead>
          <TableRow
            sx={{
              height: 44,
              "& th": { backgroundColor: "#E9E9E9", py: 0.75, px: 1.25 },
            }}
          >
            {HEADERS.map((header) => (
              <TableCell
                key={header.label}
                onClick={header.sortKey ? () => onSort(header.sortKey) : undefined}
                aria-sort={
                  sortConfig.sort_by === header.sortKey
                    ? sortConfig.sort_order === "asc"
                      ? "ascending"
                      : "descending"
                    : undefined
                }
                sx={{
                  width: header.width,
                  cursor: header.sortKey ? "pointer" : "default",
                  userSelect: header.sortKey ? "none" : "auto",
                }}
              >
                <Box display="flex" alignItems="center">
                  <Label
                    text={header.label}
                    fontSize="14px"
                    fontWeight="700"
                  />
                  {header.sortKey &&
                    (sortConfig.sort_by === header.sortKey &&
                    sortConfig.sort_order === "desc" ? (
                      <ArrowDownwardIcon
                        sx={{ ml: 0.5, fontSize: 18, color: "#154D8F" }}
                      />
                    ) : (
                      <ArrowUpwardIcon
                        sx={{
                          ml: 0.5,
                          fontSize: 18,
                          color: "#154D8F",
                          opacity:
                            sortConfig.sort_by === header.sortKey ? 1 : 0.3,
                        }}
                      />
                    ))}
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow
              key={`${row.batch_id}-${row.parameter_name}-${index}`}
              sx={
                String(row.batch_id) === String(highlightedBatchId)
                  ? {
                      "& td": {
                        backgroundColor: RECENTLY_VISITED_ROW_BACKGROUND,
                      },
                    }
                  : undefined
              }
            >
              <TableCell>
                {pageSize * (page - 1) + index + 1}
              </TableCell>
              <TableCell>
                <Label
                  text={row.unit_operation || row.step_name || "-"}
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                />
              </TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => onBatchClick(row)}
                  aria-label={`Open batch ${row.batch_id} in Manage Data`}
                  sx={{
                    p: 0,
                    border: 0,
                    background: "none",
                    cursor: "pointer",
                    font: "inherit",
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    overflow: "hidden",
                    "&:hover .label": { textDecoration: "underline" },
                    "&:focus-visible": {
                      outline: "2px solid #1B72B7",
                      outlineOffset: "2px",
                    },
                  }}
                >
                  <Label
                    text={row.batch_id || "-"}
                    color={isTruthyFlag(row.is_migrated_batch) ? "#20252D" : "#1B72B7"}
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                  />
                </Box>
              </TableCell>
              <TableCell>
                <Label
                  text={row.batch_date || row.date_of_manufacture || "-"}
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                />
              </TableCell>
              <TableCell>
                <Label text={row.missing_parameters_count ?? "-"} />
              </TableCell>
              <TableCell>
                <OverflowListSummary
                  items={normalizeMissingParameterNames(row.parameter_name)}
                />
              </TableCell>
            </TableRow>
          ))}
          {!loading && !rows.length && (
            <TableRow sx={{ height: 48 }}>
              <TableCell
                colSpan={HEADERS.length}
                align="center"
                sx={{ py: 0.5 }}
              >
                <Label
                  color="textSecondary"
                  text={
                    hasUnitOperation
                      ? "No batches found"
                      : "Select Unit Operation to view Batches"
                  }
                />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

export default React.memo(PartialDataTable);
